// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/GrowthPage.tsx (ADAPT)
// ADAPTED (plan P4-1): the `GrowthScreen` union and the `GrowthNav` context are kept for the drill-ins and back. The
// `quiz` screen (back to the onboarding questions in TP's side panel) is dropped: the page has a "Back to chat" link
// instead (AssessmentScreen). Import paths are our aliases.
/**
 * The frame every Growth Score screen shares, and how the screens move
 * between each other. A screen never navigates
 * itself: it asks the host (AssessmentScreen) to open another one.
 */
import { ArrowLeft } from 'lucide-react'
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import type { DimensionKey } from './growth-score.types'

export type GrowthScreen =
  | { kind: 'assessment' }
  | { kind: 'dimension'; dim: DimensionKey }
  | { kind: 'search' }
  | { kind: 'brand' }
  | { kind: 'cohort' }

/**
 * The assessment control that opens a screen carries `data-opener` with this key, so that Back can give it the focus
 * again (the assessment unmounts while a drill-in is open, so a remembered element would be gone). Search & AI opens
 * from the Search & AI row, like any dimension.
 */
export const OPENER_ATTR = 'data-opener'
/** Every screen's heading is focusable (`tabIndex={-1}`) and carries this attribute, so a new screen can take the focus. */
export const HEADING_ATTR = 'data-growth-heading'
export function openerKey(screen: GrowthScreen): string | null {
  switch (screen.kind) {
    case 'dimension':
      return `dimension:${screen.dim}`
    case 'search':
      return 'dimension:search'
    case 'brand':
      return 'brand'
    case 'cohort':
      return 'cohort'
    case 'assessment':
      return null
  }
}

export interface GrowthNav {
  open: (screen: GrowthScreen) => void
}

const NavCtx = createContext<GrowthNav | null>(null)

export const GrowthNavProvider = NavCtx.Provider

export function useGrowthNav(): GrowthNav {
  const nav = useContext(NavCtx)
  if (!nav) throw new Error('useGrowthNav must be used inside <GrowthNavProvider>')
  return nav
}

/** What the hero card shows beside the title: an icon, a line under it, something on the right, and a body. */
export interface GrowthHero {
  icon?: ReactNode
  subtitle?: ReactNode
  aside?: ReactNode
  children?: ReactNode
}

/**
 * One screen: a way back to the assessment, then a hero card with the title
 * (icon, subtitle, what sits on the right, the body), then the content.
 */
export function GrowthPage({
  title,
  back = false,
  actions,
  hero,
  hideTitle = false,
  children,
}: {
  title: string
  back?: boolean
  actions?: ReactNode
  hero?: GrowthHero
  /** The page already names the screen: keep the heading for screen readers only. */
  hideTitle?: boolean
  children: ReactNode
}) {
  const nav = useGrowthNav()
  const ref = useRef<HTMLDivElement>(null)
  // A new screen in the same page starts at its top.
  useEffect(() => {
    ref.current?.closest('.overflow-y-auto')?.scrollTo?.({ top: 0 })
  }, [title])
  return (
    <div ref={ref} className="flex flex-col gap-8 pb-8">
      {/* A hidden heading sits out of the flow, so it takes no gap above the first card. */}
      {hideTitle && !back && !hero ? (
        <h2 data-growth-heading="" tabIndex={-1} className="sr-only">
          {title}
        </h2>
      ) : (
        <header className="flex flex-col items-start gap-3">
          {/* Back is the quiet ghost button with the arrow, flush with the content, as elsewhere in the backoffice. */}
          {back ? (
            <Button type="button" variant="ghost" size="xs" className="-ml-2" onClick={() => nav.open({ kind: 'assessment' })}>
              <ArrowLeft aria-hidden />
              Growth Score
            </Button>
          ) : null}
          {hero ? (
            <Card className="flex w-full flex-col gap-3 p-5">
              <div className="flex flex-wrap items-center gap-3">
                {hero.icon}
                <div className="flex min-w-0 flex-col gap-0.5">
                  <h2 data-growth-heading="" tabIndex={-1} className="text-title-section text-foreground focus:outline-none">
                    {title}
                  </h2>
                  {hero.subtitle ? <p className="text-body-sm text-ds-text-2">{hero.subtitle}</p> : null}
                </div>
                {hero.aside || actions ? (
                  <div className="ml-auto flex items-center gap-3">
                    {actions}
                    {hero.aside}
                  </div>
                ) : null}
              </div>
              {hero.children}
            </Card>
          ) : (
            <div className="flex w-full flex-wrap items-center justify-between gap-3">
              <h2 data-growth-heading="" tabIndex={-1} className="text-title-section text-foreground focus:outline-none">
                {title}
              </h2>
              {actions}
            </div>
          )}
        </header>
      )}
      {children}
    </div>
  )
}

/** A section of a screen: its heading and an optional line under it. */
export function GrowthSection({ title, sub, actions, children }: { title: string; sub?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-heading-sm text-foreground">{title}</h3>
          {sub ? <p className="text-body-sm text-ds-text-2">{sub}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  )
}
